import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, CornerDownLeft } from 'lucide-react';
import { api } from '@/api/client';
import { Spinner } from '@/components/ui/Spinner';

/**
 * A3 · EL BUSCADOR GLOBAL.
 *
 * Se abre desde cualquier pantalla con **Ctrl+K** (o ⌘K) y busca en todas las
 * entidades a la vez: contactos, trabajos, pendientes, equipo y usuarios.
 *
 * ⚠️ EL TELÉFONO SE ENCUENTRA ESCRITO DE CUALQUIER FORMA. `8095551234`,
 * `809-555-1234`, `+1 809 555 1234` y `(809) 555 1234` llevan al mismo
 * contacto. Lo resuelve el server contra la columna normalizada; acá no se
 * adivina nada.
 *
 * ⚠️ TODO SE MANEJA CON TECLADO. Flechas para moverse, Enter para ir, Escape
 * para cerrar. Un buscador que se abre con un atajo y después obliga a agarrar
 * el mouse no le ahorra nada a nadie.
 *
 * ⚠️ LOS RECIENTES SON DE ESTE NAVEGADOR. Viven en `localStorage` a propósito:
 * "qué busqué yo la última vez" es una comodidad personal, no un dato del
 * negocio. Guardarlo en la base sería sincronizar entre dispositivos algo que
 * nadie pidió, y dejar rastro de búsquedas en un servidor compartido.
 */

interface ItemEncontrado {
  id: string;
  titulo: string;
  detalle?: string;
  ruta: string;
}
interface Grupo {
  tipo: string;
  etiqueta: string;
  items: ItemEncontrado[];
}
interface Resultado {
  grupos: Grupo[];
  demasiadoCorto: boolean;
  noPudeBuscarEn: string[];
}

const CLAVE_RECIENTES = 'buscador-recientes';
const MAX_RECIENTES = 6;

function leerRecientes(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_RECIENTES) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // Un navegador en modo privado o con el almacenamiento bloqueado tira acá.
    // Sin recientes el buscador anda igual: no es un error que valga contar.
    return [];
  }
}

function recordar(q: string): void {
  try {
    const previos = leerRecientes().filter((x) => x !== q);
    localStorage.setItem(CLAVE_RECIENTES, JSON.stringify([q, ...previos].slice(0, MAX_RECIENTES)));
  } catch {
    /* ver leerRecientes */
  }
}

export function BuscadorGlobal() {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [recientes, setRecientes] = useState<string[]>([]);
  const caja = useRef<HTMLInputElement>(null);

  // El atajo vive en `window` para que funcione desde cualquier pantalla.
  useEffect(() => {
    const alTecla = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierto((v) => !v);
      }
    };
    window.addEventListener('keydown', alTecla);
    return () => window.removeEventListener('keydown', alTecla);
  }, []);

  useEffect(() => {
    if (!abierto) return;
    setRecientes(leerRecientes());
    setCursor(0);
    // El `setTimeout` deja que el input exista antes de pedirle el foco.
    const t = setTimeout(() => caja.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [abierto]);

  const busqueda = useQuery({
    queryKey: ['buscar', q],
    queryFn: () => api.get<Resultado>(`/api/buscar?q=${encodeURIComponent(q)}`),
    enabled: abierto && q.trim().length >= 2,
  });

  // Todos los resultados en una sola lista, para poder moverse con las flechas
  // sin saltar entre grupos.
  const planos = (busqueda.data?.grupos ?? []).flatMap((g) =>
    g.items.map((i) => ({ ...i, etiqueta: g.etiqueta })),
  );

  const ir = useCallback(
    (item: { ruta: string }) => {
      if (q.trim()) recordar(q.trim());
      setAbierto(false);
      setQ('');
      navigate(item.ruta);
    },
    [navigate, q],
  );

  const alTeclaEnCaja = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return setAbierto(false);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(planos.length - 1, 0)));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    }
    if (e.key === 'Enter' && planos[cursor]) ir(planos[cursor]!);
  };

  if (!abierto) return null;

  const cortoAun = q.trim().length > 0 && q.trim().length < 2;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Cerrar el buscador"
        className="mac-velo absolute inset-0 cursor-default"
        onClick={() => setAbierto(false)}
      />
      <div className="mac-hoja relative mx-3 w-full max-w-xl overflow-hidden rounded-2xl text-slate-900 dark:text-slate-100">
        <div className="flex items-center gap-2 border-b border-surface-border px-4 dark:border-white/10">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            ref={caja}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCursor(0);
            }}
            onKeyDown={alTeclaEnCaja}
            placeholder="Buscar por nombre, teléfono, serie, dirección…"
            className="h-12 flex-1 bg-transparent text-sm outline-none dark:text-slate-100"
          />
          {busqueda.isFetching && <Spinner />}
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {/* Vacío: los recientes de ESTE navegador. */}
          {q.trim() === '' && (
            <>
              {recientes.length === 0 ? (
                <p className="px-2 py-3 text-xs text-slate-500">
                  Escribe un nombre, un teléfono, un número de serie o una dirección. El teléfono lo
                  encuentra escrito de cualquier forma.
                </p>
              ) : (
                <>
                  <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    Lo último que buscaste
                  </p>
                  {recientes.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setQ(r)}
                      className="block w-full rounded-[7px] px-2 py-1.5 text-left text-sm hover:bg-acento-lleno hover:text-white"
                    >
                      {r}
                    </button>
                  ))}
                </>
              )}
            </>
          )}

          {cortoAun && <p className="px-2 py-3 text-xs text-slate-500">Escribe una letra más.</p>}

          {q.trim().length >= 2 && !busqueda.isLoading && planos.length === 0 && (
            // Estado vacío que dice qué hacer, no solo "sin resultados".
            <p className="px-2 py-3 text-xs text-slate-500">
              No encontré nada con «{q.trim()}». Prueba con parte del nombre, con el número de
              teléfono completo, o con el número de serie.
            </p>
          )}

          {(busqueda.data?.grupos ?? []).map((g) => (
            <div key={g.tipo} className="mb-1">
              <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {g.etiqueta}
              </p>
              {g.items.map((item) => {
                const i = planos.findIndex((x) => x.id === item.id && x.ruta === item.ruta);
                const activo = i === cursor;
                return (
                  <button
                    key={`${g.tipo}-${item.id}`}
                    type="button"
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => ir(item)}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left ${
                      activo
                        ? 'bg-brand-50 dark:bg-slate-800'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm dark:text-slate-100">
                        {item.titulo}
                      </span>
                      {item.detalle && (
                        <span className="block truncate text-xs text-slate-500">
                          {item.detalle}
                        </span>
                      )}
                    </span>
                    {activo && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Si una tabla falló, se dice. Un buscador que calla lo que no pudo
              mirar hace creer que ahí no había nada. */}
          {(busqueda.data?.noPudeBuscarEn ?? []).length > 0 && (
            <p className="mt-1 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              No pude buscar en: {busqueda.data!.noPudeBuscarEn.join(', ')}. Lo de arriba puede
              estar incompleto.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-surface-border px-3 py-2 text-[11px] text-slate-400 dark:border-slate-800">
          <span>↑↓ moverse</span>
          <span>Enter abrir</span>
          <span>Esc cerrar</span>
        </div>
      </div>
    </div>
  );
}
